/*!
 * iLive Chapters — Phase 3.2 era rotating carousel
 *
 * 3D cylinder carousel on desktop/tablet (≥640px). Flat horizontal-snap row on
 * mobile and when prefers-reduced-motion is set. State is a single integer
 * `activeIndex` (0..4); all UI derives from it.
 *
 * Markup contract (era-selector.liquid):
 *   - .era-sel__stage    role=region, aria-roledescription=carousel, tabindex=0
 *   - .era-sel__grid     [data-era-carousel] — the rotating container
 *   - .era-sel__card     [data-index] [data-status] [data-era-slug] [data-era-name]
 *   - .era-sel__nav-prev [data-era-prev]   prev button
 *   - .era-sel__nav-next [data-era-next]   next button
 *   - [data-era-live]    visually-hidden aria-live=polite for announcements
 *
 * Locked-card click hook for Phase 3.3:
 *   When a locked era is centered and clicked, calls
 *   window.iLiveOpenChapterModal(slug, name). Phase 3.3 defines that function.
 *   Until then, silent no-op (graceful degradation).
 *
 * Depends on: window.gsap (already loaded by P3.1 via theme.liquid).
 *
 * Tunables: see CONFIG below.
 */
(function () {
  'use strict';

  // ── Tunable knobs ────────────────────────────────────────────────
  var CONFIG = {
    // Per-viewport 3D layout, keyed by ABSOLUTE distance from the center card.
    //   depth[0] = center, depth[1] = 1 card out, depth[2] = 2 out, depth[3] = 3 out.
    // render() computes a signed ring-offset for each card (shortest way around),
    // looks up depth[abs(offset)], and mirrors x/rotateY for left-side cards
    // (offset < 0). This works for ANY card count: 5 cards fan 2-per-side, 6 fan
    // 3-per-side (3 on one flank when N is even — see render() for the tie split),
    // and adding/removing a chapter needs no new positions. Cards beyond the
    // deepest defined depth clamp to the last depth entry and hide (opacity 0).
    layout: {
      desktop: {
        depth: [
          { rotateY:   0, x:    0, z:    0, scale: 1.00, opacity: 1.00, zIndex: 5 },
          { rotateY: -35, x:  180, z: -100, scale: 0.85, opacity: 0.70, zIndex: 4 },
          { rotateY: -60, x:  320, z: -200, scale: 0.70, opacity: 0.40, zIndex: 3 },
          { rotateY: -72, x:  430, z: -300, scale: 0.58, opacity: 0.16, zIndex: 2 }
        ]
      },
      tablet: {
        depth: [
          { rotateY:   0, x:    0, z:    0, scale: 1.00, opacity: 1.00, zIndex: 5 },
          { rotateY: -25, x:  130, z:  -70, scale: 0.85, opacity: 0.70, zIndex: 4 },
          { rotateY: -48, x:  220, z: -150, scale: 0.70, opacity: 0.40, zIndex: 3 },
          { rotateY: -60, x:  300, z: -230, scale: 0.58, opacity: 0.16, zIndex: 2 }
        ]
      }
    },
    tween: {
      duration: 0.6,
      ease: 'power3.out'
    },
    swipeThresholdPx: 30,
    tabletMaxPx: 1023
  };

  function ready(cb) {
    if (document.readyState !== 'loading') return cb();
    document.addEventListener('DOMContentLoaded', cb);
  }

  ready(function () {
    var stage = document.querySelector('.era-sel__stage');
    if (!stage) return;

    var grid  = stage.querySelector('[data-era-carousel]');
    var cards = Array.prototype.slice.call(stage.querySelectorAll('.era-sel__card'));
    var prev  = stage.querySelector('[data-era-prev]');
    var next  = stage.querySelector('[data-era-next]');
    var live  = stage.querySelector('[data-era-live]');
    if (!grid || cards.length === 0) return;

    var N = cards.length;
    var activeIndex = 0;
    var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var hasGSAP = !!window.gsap;

    // ── Mobile mode (<640px) or reduced motion: native scroll-snap, no 3D ──
    function isMobileMode() {
      return reduceMotion || window.innerWidth < 640;
    }

    function getLayout() {
      return window.innerWidth > CONFIG.tabletMaxPx ? CONFIG.layout.desktop : CONFIG.layout.tablet;
    }

    function announce(name) {
      if (live) live.textContent = 'Now showing: ' + name;
    }

    function setCenteredAttr() {
      cards.forEach(function (card, i) {
        if (i === activeIndex) card.setAttribute('data-centered', 'true');
        else card.removeAttribute('data-centered');
        // Non-center cards are aria-hidden for screen readers
        if (i === activeIndex) card.removeAttribute('aria-hidden');
        else card.setAttribute('aria-hidden', 'true');
      });
      var centeredName = cards[activeIndex].getAttribute('data-era-name') || '';
      announce(centeredName);
      // Dispatch for Phase 6.5 breathing tween + future hooks
      document.dispatchEvent(new CustomEvent('era:change', {
        detail: { index: activeIndex, name: centeredName }
      }));
    }

    function render(animate) {
      setCenteredAttr();

      if (isMobileMode()) {
        // Center the active card by scrolling the CAROUSEL's own horizontal
        // container — NOT the element into view. card.scrollIntoView() also
        // scrolls the whole PAGE vertically to reveal the card, which on mobile
        // yanked the visitor straight past the hero on load (the carousel sits
        // below the fold). Scrolling grid.scrollLeft only moves the carousel.
        var card = cards[activeIndex];
        if (card && grid) {
          var targetLeft = card.offsetLeft - (grid.clientWidth - card.clientWidth) / 2;
          targetLeft = Math.max(0, targetLeft);
          if (grid.scrollTo) {
            grid.scrollTo({ left: targetLeft, behavior: animate ? 'smooth' : 'auto' });
          } else {
            grid.scrollLeft = targetLeft;
          }
        }
        return;
      }

      var depth = getLayout().depth;
      var maxDepth = depth.length - 1;
      var half = N / 2;
      cards.forEach(function (card, i) {
        // Signed ring-offset: shortest way around the carousel from the center
        // card. Positive = fan out to the right, negative = to the left. For an
        // even N, the card exactly opposite (raw === N/2) resolves to the right
        // flank so it fans out instead of hiding dead-behind center.
        var raw = (i - activeIndex + N) % N;
        var offset = raw > half ? raw - N : raw;
        var dist = Math.abs(offset);
        if (dist > maxDepth) dist = maxDepth; // clamp deep cards to the last ring
        var d = depth[dist];
        // depth[] is authored for the RIGHT side; mirror x + rotateY for the left.
        var side = offset < 0 ? -1 : 1;
        var p = {
          x: d.x * side,
          z: d.z,
          rotateY: d.rotateY * side,
          scale: d.scale,
          opacity: d.opacity,
          zIndex: d.zIndex
        };
        var props = {
          x: p.x,
          z: p.z,
          rotationY: p.rotateY,
          scale: p.scale,
          opacity: p.opacity,
          zIndex: p.zIndex
        };
        if (hasGSAP && animate) {
          gsap.to(card, Object.assign({ duration: CONFIG.tween.duration, ease: CONFIG.tween.ease, overwrite: 'auto' }, props));
        } else if (hasGSAP) {
          gsap.set(card, props);
        } else {
          // No GSAP available (shouldn't happen — P3.1 loads it). Snap via inline style as a fallback.
          card.style.transform = 'translate(-50%, 0) translate3d(' + p.x + 'px, 0, ' + p.z + 'px) rotateY(' + p.rotateY + 'deg) scale(' + p.scale + ')';
          card.style.opacity = String(p.opacity);
          card.style.zIndex = String(p.zIndex);
        }
      });
    }

    function step(dir) {
      activeIndex = (activeIndex + dir + N) % N;
      render(true);
    }

    function goTo(idx) {
      if (idx < 0 || idx >= N || idx === activeIndex) return;
      // Choose shortest rotation direction
      var forward = (idx - activeIndex + N) % N;
      var backward = (activeIndex - idx + N) % N;
      activeIndex = idx;
      // The actual GSAP tween animates from current to target positions, which naturally takes the
      // shortest visual path because we re-evaluate (i - activeIndex) per card. forward/backward is
      // informational only — left here in case we want to swing multiple steps with intermediate frames later.
      render(true);
    }

    // ── Handlers ────────────────────────────────────────────────
    if (prev) prev.addEventListener('click', function () { step(-1); });
    if (next) next.addEventListener('click', function () { step(+1); });

    // Click a side card to bring it to center; clicked locked-centered card opens modal hook.
    cards.forEach(function (card, i) {
      card.addEventListener('click', function (e) {
        if (i !== activeIndex) {
          e.preventDefault();
          goTo(i);
          return;
        }
        // i === activeIndex: card is already centered
        if (card.getAttribute('data-status') === 'locked') {
          e.preventDefault();
          var slug = card.getAttribute('data-era-slug') || '';
          var name = card.getAttribute('data-era-name') || '';
          if (typeof window.iLiveOpenChapterModal === 'function') {
            window.iLiveOpenChapterModal(slug, name);
          }
          // Else: Phase 3.3 not loaded yet — silent no-op.
          return;
        }
        // Active centered card: let its <a href> navigate normally.
      });
    });

    // Keyboard navigation when stage has focus
    stage.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); step(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); step(+1); }
    });

    // Touch swipe (works on desktop too, but the trigger is touch-only)
    var touchStartX = null;
    grid.addEventListener('touchstart', function (e) {
      touchStartX = e.touches[0].clientX;
    }, { passive: true });
    grid.addEventListener('touchend', function (e) {
      if (touchStartX === null) return;
      var dx = (e.changedTouches[0].clientX - touchStartX);
      touchStartX = null;
      if (Math.abs(dx) >= CONFIG.swipeThresholdPx) {
        // Swipe left → next; swipe right → prev (mirrors native iOS gallery behavior)
        step(dx < 0 ? +1 : -1);
      }
    }, { passive: true });

    // Re-render on viewport resize (mode may flip between mobile/desktop)
    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { render(false); }, 100);
    });

    // Initial render (no animation — set straight to final state)
    render(false);
    // Phase 6.6 hotfix: mark grid ready so the opacity:0 first-paint CSS
    // (which hides the stacked-at-center flash) releases control to GSAP.
    grid.classList.add('era-sel__grid--ready');
  });
})();
